import React, { useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Alert,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Visibility,
  ShoppingCart,
  Inventory,
  LocalShipping,
  Assessment,
  Warning,
  CheckCircle,
  Schedule,
  AttachMoney,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useStore } from "../../store/useStore";
import { useNotification } from "../../components/common/NotificationProvider";
import { useFormValidation, commonValidationRules } from "../../hooks/useFormValidation";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`procurement-tabpanel-${index}`}
      aria-labelledby={`procurement-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const Procurement: React.FC = () => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useNotification();
  const { suppliers, addSupplier, updateSupplier, deleteSupplier, purchaseOrders } = useStore();
  const [tabValue, setTabValue] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState("");
  const [editingSupplier, setEditingSupplier] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: "",
    contact: "",
    email: "",
    phone: "",
    category: "",
    rating: "",
    status: "Active",
  });

  const validationRules = {
    name: commonValidationRules.name,
    contact: { required: true, minLength: 2 },
    email: { required: true, pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/ },
    phone: { required: true, minLength: 10 },
    category: { required: true },
    rating: { required: true, min: 1, max: 5 },
  };

  const { errors, validateForm, validateFieldOnChange, clearErrors } = useFormValidation(validationRules);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleOpenDialog = (type: string, supplier?: any) => {
    setDialogType(type);
    if (supplier) {
      setEditingSupplier(supplier);
      setFormData({
        name: supplier.name,
        contact: supplier.contact,
        email: supplier.email,
        phone: supplier.phone,
        category: supplier.category,
        rating: supplier.rating.toString(),
        status: supplier.status,
      });
    } else {
      setEditingSupplier(null);
      setFormData({
        name: "",
        contact: "",
        email: "",
        phone: "",
        category: "",
        rating: "",
        status: "Active",
      });
    }
    clearErrors();
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setDialogType("");
    setEditingSupplier(null);
    setFormData({
      name: "",
      contact: "",
      email: "",
      phone: "",
      category: "",
      rating: "",
      status: "Active",
    });
    clearErrors();
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    validateFieldOnChange(field, value);
  };

  const handleSubmit = () => {
    if (!validateForm(formData)) {
      showError("Please fix the form errors before submitting");
      return;
    }

    try {
      const supplierData = {
        name: formData.name,
        contact: formData.contact,
        email: formData.email,
        phone: formData.phone,
        category: formData.category,
        rating: parseFloat(formData.rating),
        status: formData.status,
        totalOrders: editingSupplier?.totalOrders || 0,
        lastOrder: editingSupplier?.lastOrder || "N/A",
      };

      if (editingSupplier) {
        updateSupplier(editingSupplier.id, supplierData);
        showSuccess("Supplier updated successfully");
      } else {
        addSupplier(supplierData);
        showSuccess("Supplier added successfully");
      }
      handleCloseDialog();
    } catch (error) {
      showError("Failed to save supplier");
    }
  };

  const handleDelete = (id: number) => {
    try {
      deleteSupplier(id);
      showSuccess("Supplier deleted successfully");
    } catch (error) {
      showError("Failed to delete supplier");
    }
  };

  // Use store data for purchase orders
  const purchaseOrdersData = purchaseOrders;
  
  // Mock data for other sections
  const purchaseRequisitionsData = [
    {
      id: 1,
      item: "Surgical Gloves (Box of 100)",
      quantity: 50,
      unitPrice: 25,
      total: 1250,
      department: "Surgery",
      requestedBy: "Dr. Sarah Al-Rashid",
      status: "Pending Approval",
      priority: "High",
      dateRequested: "2024-01-15",
    },
    {
      id: 2,
      item: "MRI Contrast Agent",
      quantity: 20,
      unitPrice: 150,
      total: 3000,
      department: "Radiology",
      requestedBy: "Dr. Ahmed Hassan",
      status: "Approved",
      priority: "Medium",
      dateRequested: "2024-01-14",
    },
    {
      id: 3,
      item: "Blood Pressure Monitors",
      quantity: 10,
      unitPrice: 200,
      total: 2000,
      department: "Cardiology",
      requestedBy: "Nurse Fatima",
      status: "Ordered",
      priority: "Low",
      dateRequested: "2024-01-13",
    },
  ];

  // Use store data for suppliers
  const suppliersData = suppliers;

  const inventoryData = [
    {
      id: 1,
      item: "Surgical Masks",
      category: "PPE",
      currentStock: 150,
      minStock: 50,
      maxStock: 500,
      unitCost: 2.5,
      totalValue: 375,
      status: "In Stock",
      lastRestocked: "2024-01-10",
    },
    {
      id: 2,
      item: "Insulin Vials",
      category: "Pharmaceuticals",
      currentStock: 25,
      minStock: 30,
      maxStock: 100,
      unitCost: 45,
      totalValue: 1125,
      status: "Low Stock",
      lastRestocked: "2024-01-05",
    },
    {
      id: 3,
      item: "X-Ray Film",
      category: "Imaging Supplies",
      currentStock: 0,
      minStock: 20,
      maxStock: 200,
      unitCost: 15,
      totalValue: 0,
      status: "Out of Stock",
      lastRestocked: "2023-12-20",
    },
  ];


  const getStatusColor = (status: string) => {
    switch (status) {
      case "Approved":
      case "Delivered":
      case "In Stock":
      case "Active":
        return "success";
      case "Pending Approval":
      case "Processing":
      case "In Transit":
      case "Low Stock":
        return "warning";
      case "Out of Stock":
      case "Inactive":
        return "error";
      default:
        return "default";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "High":
        return "error";
      case "Medium":
        return "warning";
      case "Low":
        return "success";
      default:
        return "default";
    }
  };

  const lowStockAlerts = inventoryData.filter(
    (item) => item.status === "Low Stock" || item.status === "Out of Stock"
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t("procurement.title")}
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Comprehensive procurement and supply chain management
      </Typography>

      {/* Alerts */}
      {lowStockAlerts.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Inventory Alerts
          </Typography>
          {lowStockAlerts.map((item) => (
            <Typography key={item.id} variant="body2">
              • {item.item} - {item.status} (Current: {item.currentStock}, Min:{" "}
              {item.minStock})
            </Typography>
          ))}
        </Alert>
      )}

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 3, width: "100%" }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <ShoppingCart color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">15</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending Orders
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Inventory color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">1,250</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Items
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <AttachMoney color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">SAR 45K</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Monthly Spend
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <LocalShipping color="info" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">8</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Suppliers
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Purchase Requisitions" />
            <Tab label="Purchase Orders" />
            <Tab label="Suppliers" />
            <Tab label="Inventory" />
            <Tab label="Reports" />
          </Tabs>
        </Box>

        {/* Purchase Requisitions Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Purchase Requisitions</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog("requisition")}
            >
              New Requisition
            </Button>
          </Box>
          <TableContainer component={Paper} sx={{ width: "100%" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell align="right">Unit Price</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Requested By</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {purchaseRequisitionsData.map((req) => (
                  <TableRow key={req.id}>
                    <TableCell>{req.item}</TableCell>
                    <TableCell align="right">{req.quantity}</TableCell>
                    <TableCell align="right">SAR {req.unitPrice}</TableCell>
                    <TableCell align="right">SAR {req.total}</TableCell>
                    <TableCell>{req.department}</TableCell>
                    <TableCell>{req.requestedBy}</TableCell>
                    <TableCell>
                      <Chip
                        label={req.status}
                        color={getStatusColor(req.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={req.priority}
                        color={getPriorityColor(req.priority) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small">
                        <Visibility />
                      </IconButton>
                      <IconButton size="small">
                        <Edit />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Purchase Orders Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Purchase Orders</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog("order")}
            >
              Create Order
            </Button>
          </Box>
          <TableContainer component={Paper} sx={{ width: "100%" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Order ID</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell align="right">Items</TableCell>
                  <TableCell align="right">Total Amount</TableCell>
                  <TableCell>Order Date</TableCell>
                  <TableCell>Expected Delivery</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {purchaseOrdersData.map((order) => (
                  <TableRow key={order.id}>
                    <TableCell>
                      PO-{order.id.toString().padStart(4, "0")}
                    </TableCell>
                    <TableCell>{order.supplier}</TableCell>
                    <TableCell align="right">{order.items}</TableCell>
                    <TableCell align="right">
                      SAR {order.totalAmount.toLocaleString()}
                    </TableCell>
                    <TableCell>{order.orderDate}</TableCell>
                    <TableCell>{order.expectedDelivery}</TableCell>
                    <TableCell>
                      <Chip
                        label={order.status}
                        color={getStatusColor(order.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={order.priority}
                        color={getPriorityColor(order.priority) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small">
                        <Visibility />
                      </IconButton>
                      <IconButton size="small">
                        <Edit />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Suppliers Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Supplier Management</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog("supplier")}
            >
              Add Supplier
            </Button>
          </Box>
          <TableContainer component={Paper} sx={{ width: "100%" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">Rating</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Total Orders</TableCell>
                  <TableCell>Last Order</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {suppliers.map((supplier) => (
                  <TableRow key={supplier.id}>
                    <TableCell>{supplier.name}</TableCell>
                    <TableCell>{supplier.contact}</TableCell>
                    <TableCell>{supplier.email}</TableCell>
                    <TableCell>{supplier.phone}</TableCell>
                    <TableCell>{supplier.category}</TableCell>
                    <TableCell align="right">{supplier.rating}/5</TableCell>
                    <TableCell>
                      <Chip
                        label={supplier.status}
                        color={getStatusColor(supplier.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">{supplier.totalOrders}</TableCell>
                    <TableCell>{supplier.lastOrder}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog("supplier", supplier)}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(supplier.id)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Inventory Tab */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Inventory Management</Typography>
            <Button
              variant="contained"
              startIcon={<Inventory />}
              onClick={() => handleOpenDialog("inventory")}
            >
              Add Item
            </Button>
          </Box>
          <TableContainer component={Paper} sx={{ width: "100%" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">Current Stock</TableCell>
                  <TableCell align="right">Min Stock</TableCell>
                  <TableCell align="right">Max Stock</TableCell>
                  <TableCell align="right">Unit Cost</TableCell>
                  <TableCell align="right">Total Value</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Restocked</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {inventoryData.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.item}</TableCell>
                    <TableCell>{item.category}</TableCell>
                    <TableCell align="right">{item.currentStock}</TableCell>
                    <TableCell align="right">{item.minStock}</TableCell>
                    <TableCell align="right">{item.maxStock}</TableCell>
                    <TableCell align="right">SAR {item.unitCost}</TableCell>
                    <TableCell align="right">SAR {item.totalValue}</TableCell>
                    <TableCell>
                      <Chip
                        label={item.status}
                        color={getStatusColor(item.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{item.lastRestocked}</TableCell>
                    <TableCell>
                      <IconButton size="small">
                        <Visibility />
                      </IconButton>
                      <IconButton size="small">
                        <Edit />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Reports Tab */}
        <TabPanel value={tabValue} index={4}>
          <Typography variant="h6" gutterBottom>
            Procurement Reports
          </Typography>
          <Grid container spacing={2} sx={{ width: "100%" }}>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Spending Analysis
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Monthly and quarterly spending reports by category
                  </Typography>
                  <Button variant="outlined" fullWidth>
                    Generate Report
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Supplier Performance
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Supplier ratings, delivery times, and quality metrics
                  </Typography>
                  <Button variant="outlined" fullWidth>
                    Generate Report
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Inventory Reports
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Stock levels, turnover rates, and reorder recommendations
                  </Typography>
                  <Button variant="outlined" fullWidth>
                    Generate Report
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Card>

      {/* Dialog for adding new items */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {dialogType === "requisition" && "New Purchase Requisition"}
          {dialogType === "order" && "Create Purchase Order"}
          {dialogType === "supplier" && (editingSupplier ? "Edit Supplier" : "Add New Supplier")}
          {dialogType === "inventory" && "Add Inventory Item"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {dialogType === "supplier" ? (
              <>
                <TextField
                  fullWidth
                  label="Supplier Name"
                  variant="outlined"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  error={!!errors.name}
                  helperText={errors.name}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Contact Person"
                  variant="outlined"
                  value={formData.contact}
                  onChange={(e) => handleInputChange("contact", e.target.value)}
                  error={!!errors.contact}
                  helperText={errors.contact}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  variant="outlined"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  error={!!errors.email}
                  helperText={errors.email}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Phone"
                  variant="outlined"
                  value={formData.phone}
                  onChange={(e) => handleInputChange("phone", e.target.value)}
                  error={!!errors.phone}
                  helperText={errors.phone}
                  sx={{ mb: 2 }}
                />
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Category</InputLabel>
                  <Select 
                    label="Category"
                    value={formData.category}
                    onChange={(e) => handleInputChange("category", e.target.value)}
                  >
                    <MenuItem value="Medical Supplies">Medical Supplies</MenuItem>
                    <MenuItem value="Pharmaceuticals">Pharmaceuticals</MenuItem>
                    <MenuItem value="Medical Equipment">Medical Equipment</MenuItem>
                    <MenuItem value="Laboratory Supplies">Laboratory Supplies</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Rating (1-5)"
                  type="number"
                  variant="outlined"
                  value={formData.rating}
                  onChange={(e) => handleInputChange("rating", e.target.value)}
                  error={!!errors.rating}
                  helperText={errors.rating}
                  inputProps={{ min: 1, max: 5, step: 0.1 }}
                  sx={{ mb: 2 }}
                />
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Status</InputLabel>
                  <Select 
                    label="Status"
                    value={formData.status}
                    onChange={(e) => handleInputChange("status", e.target.value)}
                  >
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </>
            ) : (
              <>
                <TextField
                  fullWidth
                  label="Item/Supplier Name"
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Quantity/Contact"
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Category/Status</InputLabel>
                  <Select label="Category/Status">
                    <MenuItem value="medical">Medical Supplies</MenuItem>
                    <MenuItem value="pharmaceutical">Pharmaceuticals</MenuItem>
                    <MenuItem value="equipment">Equipment</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={dialogType === "supplier" ? handleSubmit : handleCloseDialog}>
            {dialogType === "supplier" ? (editingSupplier ? "Update" : "Add") : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Procurement;
